import { useEffect, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { Play } from "lucide-react";

const STORAGE_KEY = "plena_tour_seen_v1";

const COMMON_STYLES = {
  options: {
    arrowColor: "#ffffff",
    backgroundColor: "#ffffff",
    overlayColor: "rgba(15, 23, 42, 0.55)",
    primaryColor: "#0ea5e9",
    textColor: "#0f172a",
    zIndex: 10000,
    width: 380,
  },
  tooltipContainer: { textAlign: "left" },
  buttonNext: {
    backgroundColor: "#0ea5e9",
    color: "#fff",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
  },
  buttonBack: { color: "#475569", fontSize: 13, marginRight: 8 },
  buttonSkip: { color: "#94a3b8", fontSize: 12 },
};

const ROLE_TO_STEPS = {
  selector: [
    {
      target: "body",
      placement: "center",
      title: "Plena Growth Demo Turu",
      content:
        "Hoş geldin! Sana yapay zekâ destekli kariyer gelişim platformumuzu tanıtacağım. 3 farklı persona ile sistemi inceleyebilirsin: Çalışan, Yönetici, İK.",
      disableBeacon: true,
    },
    {
      target: "#persona-employee-card",
      title: "1️⃣ Çalışan Görünümü",
      content:
        "Çalışan, kariyer hedefi, hazır olma skoru, AI önerili öğrenme yolu ve mentor eşleştirme görür. Şimdi tıklayıp keşfedebilirsin.",
    },
    {
      target: "#persona-manager-card",
      title: "2️⃣ Yönetici Görünümü",
      content:
        "Yönetici, ekip risk radarı, mentor talepleri ve proaktif eğitim önerileri tetikleyicisi ile her gün hangi aksiyonu alacağını net görür.",
    },
    {
      target: "#persona-hr-card",
      title: "3️⃣ İK Komuta Merkezi",
      content:
        "İK; kurum genelinde mentor onaylarını, yedekleme planlamasını, kritik rolleri ve ayrılma riskini tek panodan yönetir.",
    },
  ],
  dashboard: [
    {
      target: '[data-testid="next-actions-card"]',
      title: "🎯 Bugün Sırada Ne Var?",
      content:
        "Yapay zekâ; bekleyen mentor talepleri, eğitim önerileri ve yüksek riskli üyeleri tek kartta önceliklendirir. Tek tıkla ilgili akışa zıplarsın.",
      disableBeacon: true,
    },
    {
      target: '[data-testid="ai-insight-card"]',
      title: "✨ AI İçgörüleri",
      content:
        "Plena AI; performans × bağlılık × beceri açığı sinyallerini birleştirerek somut, eylemli içgörü üretir. Her içgörü tıklanarak detayına gidilebilir.",
    },
    {
      target: 'body',
      placement: "center",
      title: "✅ Tur Tamamlandı",
      content:
        "Artık platformu serbestçe gezebilirsin. Bu turu istediğin zaman sağ üstteki '🎬 Demo Turu' butonundan tekrar başlatabilirsin.",
    },
  ],
};

/**
 * GuidedTour
 * - Auto-starts the first time a user lands on a persona selector or dashboard
 *   (tracked via localStorage). Can be manually restarted via TourTrigger.
 * - Scope: "selector" or "dashboard" — different step sets.
 */
export default function GuidedTour({ scope = "selector", autoStart = true }) {
  const [run, setRun] = useState(false);
  const steps = ROLE_TO_STEPS[scope] || [];

  useEffect(() => {
    if (!autoStart) return;
    const key = `${STORAGE_KEY}_${scope}`;
    const seen = localStorage.getItem(key);
    if (!seen) {
      const t = setTimeout(() => setRun(true), 700);
      return () => clearTimeout(t);
    }
  }, [scope, autoStart]);

  // Listen for manual restart event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.scope === scope || !e.detail?.scope) {
        setRun(true);
      }
    };
    window.addEventListener("plena:restart-tour", handler);
    return () => window.removeEventListener("plena:restart-tour", handler);
  }, [scope]);

  const handleCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem(`${STORAGE_KEY}_${scope}`, "1");
      setRun(false);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableScrolling={false}
      callback={handleCallback}
      locale={{
        back: "Geri",
        close: "Kapat",
        last: "Bitir",
        next: "İleri",
        skip: "Atla",
      }}
      styles={COMMON_STYLES}
    />
  );
}

/** Header button to manually restart the tour for the current scope. */
export function TourTrigger({ scope = "selector", label = "Demo Turu" }) {
  const onClick = () => {
    // Allow restart even if previously dismissed
    localStorage.removeItem(`${STORAGE_KEY}_${scope}`);
    window.dispatchEvent(new CustomEvent("plena:restart-tour", { detail: { scope } }));
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="tour-trigger-btn"
      className="inline-flex items-center gap-1.5 text-[0.7rem] font-medium text-slate-600 hover:text-sky-600 px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-sky-300 hover:bg-sky-50 transition"
      title="Rehberli demo turunu yeniden başlat"
    >
      <Play className="w-3 h-3" />
      {label}
    </button>
  );
}
